import { QueryClient } from '@tanstack/react-query'
import { log } from '@/utils/logger'

/**
 * Centralized Network Switch Manager
 *
 * Handles network switching logic and coordinates cache invalidation
 * across the entire application. This ensures:
 * - Single source of truth for network switching
 * - Automatic query invalidation across all components
 * - No stale data when switching networks
 */
export class NetworkSwitchManager {
  private static queryClient: QueryClient | null = null

  /**
   * Initialize the network switch manager with a QueryClient instance
   * Must be called during app initialization
   */
  static initialize(queryClient: QueryClient): void {
    this.queryClient = queryClient
    log.info('NetworkSwitchManager initialized')
  }

  /**
   * Handle network change event
   * Invalidates all network-specific queries to ensure fresh data
   */
  static handleNetworkChange(newNetwork: string, oldNetwork?: string): void {
    if (!this.queryClient) {
      log.warn('NetworkSwitchManager not initialized, cannot invalidate queries')
      return
    }

    log.info(`Network switch: ${oldNetwork || 'unknown'} -> ${newNetwork}`)

    // Invalidate all network-specific queries
    // Using predicate to selectively invalidate queries that depend on network
    this.queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as string[]

        // Invalidate queries that include network identifiers or are network-dependent
        const networkDependentKeys = [
          'validators',
          'blocks',
          'transactions',
          'epoch-info',
          'vote-accounts',
          'cluster-nodes',
          'recent-blocks',
          'recent-transactions',
          'network-stats',
          'validator-details',
          'block-details',
          'transaction-details',
          'account-details'
        ]

        // Check if any part of the query key matches network-dependent data
        return key.some(k => {
          if (typeof k === 'string') {
            // Check if key contains the network name or any network-dependent keyword
            return k === newNetwork ||
                   k === oldNetwork ||
                   networkDependentKeys.some(dep => k.includes(dep))
          }
          return false
        })
      }
    })

    log.info('React Query cache invalidated for network switch')
  }

  /**
   * Get the current QueryClient instance
   */
  static getQueryClient(): QueryClient | null {
    return this.queryClient
  }

  /**
   * Force invalidate all queries (nuclear option)
   * Use sparingly, only when absolutely necessary
   */
  static invalidateAll(): void {
    if (!this.queryClient) {
      log.warn('NetworkSwitchManager not initialized, cannot invalidate queries')
      return
    }

    this.queryClient.invalidateQueries()
    log.info('All queries invalidated')
  }
}
